/**
 * ProductComposition custom element
 * Displays composition information for selected product options
 * Subscribes to variant selection changes via PubSub
 */

class ProductComposition extends HTMLElement {
  constructor() {
    super();
    this.compositionData = {};
    this.sectionId = null;
    this.unsubscriber = null;
    this.contentContainer = null;
  }

  connectedCallback() {
    // Get section ID
    this.sectionId = this.dataset.sectionId;
    if (!this.sectionId) {
      console.error('ProductComposition: Missing section ID');
      return;
    }

    // Get content container
    this.contentContainer = this.querySelector('.product-composition__content-wrapper');
    if (!this.contentContainer) {
      console.error('ProductComposition: Missing content container');
      return;
    }

    // Parse composition data
    try {
      const compositionDataRaw = this.dataset.compositionData;
      this.compositionData = JSON.parse(compositionDataRaw);
    } catch (e) {
      console.error('ProductComposition: Failed to parse composition data', e);
      this.compositionData = {};
    }

    // Subscribe to variant selection changes
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleVariantChange.bind(this));
    }

    // Check initial state
    this.updateDisplay();
  }

  disconnectedCallback() {
    if (this.unsubscriber) {
      this.unsubscriber();
    }
  }

  /**
   * Handle variant selection change event
   * @param {Object} event - The event data from PubSub
   */
  handleVariantChange(event) {
    // Only respond to changes in our section
    const variantSelects = event?.data?.target?.closest('variant-selects');
    if (!variantSelects || !variantSelects.id.includes(this.sectionId)) {
      return;
    }

    this.updateDisplay();
  }

  /**
   * Get all currently selected option values
   * @returns {Array<{value: string, optionName: string}>}
   */
  getSelectedValues() {
    const variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
    if (!variantSelects) {
      return [];
    }

    const selectedValues = [];

    // Get values from radio inputs (swatch selections)
    const radioInputs = variantSelects.querySelectorAll('input[type="radio"]:checked');
    radioInputs.forEach(input => {
      const fieldset = input.closest('fieldset');
      const legend = fieldset?.querySelector('.form__label');
      const optionName = legend?.textContent?.trim() || '';
      
      selectedValues.push({
        value: input.value.trim(),
        optionName: optionName
      });
    });

    // Get values from select dropdowns
    const selectElements = variantSelects.querySelectorAll('select');
    selectElements.forEach(select => {
      const selectedOption = select.options[select.selectedIndex];
      const label = select.closest('.product-form__input')?.querySelector('label');
      const optionName = label?.textContent?.trim() || '';
      
      if (selectedOption) {
        selectedValues.push({
          value: selectedOption.value.trim(),
          optionName: optionName
        });
      }
    });

    return selectedValues;
  }

  /**
   * Find compositions for selected values
   * @returns {Array<{value: string, composition: string}>}
   */
  findMatchedCompositions() {
    const selectedValues = this.getSelectedValues();
    const matched = [];

    for (const selected of selectedValues) {
      // Check if we have composition data for this value
      const compositionInfo = this.compositionData[selected.value];
      if (compositionInfo && compositionInfo.composition) {
        matched.push({
          value: selected.value,
          optionName: compositionInfo.optionName,
          composition: compositionInfo.composition
        });
      }
    }

    return matched;
  }

  /**
   * Update the display based on current selections
   */
  updateDisplay() {
    const matchedCompositions = this.findMatchedCompositions();

    if (!this.contentContainer) return;

    // Clear existing content
    this.contentContainer.innerHTML = '';

    if (matchedCompositions.length > 0) {
      // Add composition content for each matched value
      matchedCompositions.forEach(item => {
        const itemDiv = document.createElement('div');
        itemDiv.className = 'product-composition__item';
        itemDiv.setAttribute('data-option', item.optionName);
        itemDiv.setAttribute('data-value', item.value);
        
        const contentDiv = document.createElement('div');
        contentDiv.className = 'product-composition__content';
        contentDiv.innerHTML = item.composition;
        
        itemDiv.appendChild(contentDiv);
        this.contentContainer.appendChild(itemDiv);
      });
    }
  }
}

// Define the custom element
customElements.define('product-composition', ProductComposition);

